/*
 * Decléor — Smooth scrolling (Lenis)
 * ---------------------------------------------------------------------------
 * Boots Lenis (https://github.com/darkroomengineering/lenis) on the window, from the
 * self-hosted build at assets/lenis.js. Its URL is passed in through data-lenis-src on
 * this script's own <script> tag (same pattern as quantity-number-flow.js → number-flow.js),
 * so the library is only fetched when smooth scrolling is actually enabled and the visitor
 * has not asked for reduced motion.
 *
 * WHY LENIS IS SAFE HERE — the theme keeps its native scroll position
 * ---------------------------------------------------------------------------
 * Lenis with `wrapper: window` does NOT transform a wrapper element: it lerps the real
 * window scroll offset through window.scrollTo({behavior:'instant'}). So everything in
 * Pipeline that reads the native scroll keeps working untouched:
 *   - the theme:scroll / :up / :down bus (assets/theme.js listens to the native `scroll`),
 *   - the sticky header, AOS reveals, Rellax parallax, sticky-atc.js,
 *   - position: sticky (no ancestor transform is introduced).
 * We never edit theme.js — this file only consumes/complements it.
 *
 * NOT BREAKING THE HORIZONTAL SCROLLERS — the important part
 * ---------------------------------------------------------------------------
 * The theme is full of native horizontal scrollers, all built on `overflow-x: auto`
 * (+ scroll-snap) rather than a carousel library: `.tabs` / [data-scrollbar-slider]
 * (product tabs, related, collection tabs, blog tags, header blocks),
 * [data-custom-scrollbar-items], .media__thumb__wrapper (product thumbnails),
 * .cart-upsell__track (snippets/luxe-overrides.liquid), .grid--double
 * (sections/section-icons.liquid), .overflow-x-auto (sections/section-tabs.liquid),
 * sections/table.liquid, sections/search-tags.liquid.
 *
 * Two things protect them:
 *   1. `allowNestedScroll: true` — before hijacking a wheel event Lenis walks the
 *      composedPath and, if an ancestor is a scroll container that can still scroll in
 *      the dominant gesture direction, it bails out and lets the browser scroll it
 *      natively. That is what saves a trackpad swipe over a carousel (which emits BOTH
 *      deltaX and deltaY — the classic "smooth scroll ate my slider" bug), and it also
 *      honours `overscroll-behavior-x: contain` on those tracks. Once the track hits its
 *      end, the page scrolls again — the native chaining behaviour.
 *   2. `syncTouch: false` (the Lenis default, kept deliberately) — on touch devices Lenis
 *      stays completely passive and the browser owns the gesture, so CSS scroll-snap,
 *      swipe momentum and the mobile sliders behave exactly as before. Smoothing is
 *      wheel/trackpad only, which is also where it is actually wanted.
 * Anything else can opt out with `data-lenis-prevent` (or -vertical / -horizontal / -touch
 * / -wheel) on the element — a Lenis built-in.
 *
 * SCROLL LOCK — drawers, modals, the PhotoSwipe lightbox
 * ---------------------------------------------------------------------------
 * The theme locks the page through the scroll-lock lib and marks it with
 * `data-scroll-locked` on <html> (see the theme:scroll:lock/:unlock handlers in theme.js).
 * We mirror that attribute onto lenis.stop()/start() so the inertia can never fight a lock
 * or drift while a drawer is open. Because the nested-scroll check above runs BEFORE the
 * stopped check inside Lenis, the drawer's own scrollable body still scrolls while stopped.
 *
 * ANCHORS
 * ---------------------------------------------------------------------------
 * theme.css sets `html { scroll-behavior: smooth }`, which we have to neutralise for Lenis
 * (see snippets/smooth-scroll-style.liquid) — so in-page links would otherwise become hard
 * jumps. We re-implement them here instead of using Lenis's own `anchors` option, because
 * we need two things it can't give us: the live sticky-header offset, and a bail-out on
 * links a theme handler already owns (`#recover` on the login page, `#!` in the header).
 *
 * CALIBRATED AGAINST respire.co (the reference the brand asked for)
 * ---------------------------------------------------------------------------
 * respire.co is also a Shopify store running Lenis (1.3.4, bundled by Vite). Its entire
 * configuration, read out of its live `vite-main` bundle, is:
 *     window._lenis = new Lenis({ anchors: { offset: -50 } })
 * i.e. STOCK DEFAULTS for everything that governs the feel — and the defaults in its version
 * are numerically the same as in ours: `lerp: 0.1`, `wheelMultiplier: 1`, `smoothWheel: true`,
 * `syncTouch: false`, `gestureOrientation: 'vertical'`, `overscroll: true`. So the wheel/
 * trackpad response below is theirs, notch for notch. The one feel-level extra they ship is in
 * CSS (`overscroll-behavior: contain` on the root) — mirrored in smooth-scroll-style.liquid.
 *
 * Three things we deliberately do NOT copy from them:
 *   - `allowNestedScroll` left at its default `false`. respire.co has five
 *     `overflow-x-auto snap-x` sliders and puts `data-lenis-prevent` on none of them (only on
 *     its three drawer panes), so a trackpad gesture with any vertical component over a slider
 *     scrolls its page instead of the slider. macOS axis-locks deliberate horizontal swipes,
 *     which hides it most of the time — on a Windows precision touchpad it does not. This
 *     theme has a dozen-plus such tracks, so we keep the option on.
 *   - Ignoring `prefers-reduced-motion`. Their store records the media query and never acts on
 *     it (no reduced-motion CSS at all), so smooth scrolling stays on for everyone.
 *   - Their scroll lock: destroy Lenis, pin `body { position: fixed; top: -Ypx }`, re-create it
 *     and `scrollTo(saved, {immediate:true})` on unlock. This theme already owns a scroll-lock
 *     library with scrollbar gap-filling; cooperating with it via stop()/start() is both less
 *     code and less likely to double-compensate.
 * Their `anchors: { offset: -50 }` is also not portable: they never set
 * `html { scroll-behavior: smooth }`, and a flat 50px does not clear this theme's ~100px
 * sticky header — hence the measured offset below.
 *
 * Degrades to plain native scrolling on every failure path: no <script data-lenis-src>,
 * no ResizeObserver, prefers-reduced-motion, the merchant toggle off, `data-lenis-disabled`
 * on <html>, or assets/lenis.js failing to load.
 */
(function () {
  'use strict';

  var tag = document.querySelector('script[data-lenis-src]');
  if (!tag) { return; }

  var SRC = tag.getAttribute('data-lenis-src');
  if (!SRC) { return; }
  if (tag.getAttribute('data-lenis-enabled') === 'false') { return; }
  // Per-template escape hatch: <html data-lenis-disabled>
  if (document.documentElement.hasAttribute('data-lenis-disabled')) { return; }
  // Lenis needs ResizeObserver; this also keeps its modern-syntax bundle away from IE11.
  if (typeof window.ResizeObserver !== 'function') { return; }

  var reduceMQ = window.matchMedia('(prefers-reduced-motion: reduce)');
  if (reduceMQ.matches) { return; }

  var html = document.documentElement;
  var lenis = null;
  var restoreScrollTo = null;
  var lockObserver = null;

  // ---- Settings (theme editor → data-attributes on our own <script>) -----
  function num(attr, fallback, min, max) {
    var v = parseFloat(tag.getAttribute(attr));
    if (isNaN(v)) { return fallback; }
    return Math.min(max, Math.max(min, v));
  }
  var LERP = num('data-lenis-lerp', 0.1, 0.02, 0.5);
  var WHEEL = num('data-lenis-wheel', 1, 0.25, 2);
  var ANCHORS = tag.getAttribute('data-lenis-anchors') !== 'false';

  // ---- Elements that must always keep the browser's own gesture handling -
  // PhotoSwipe drives its own wheel/pinch handling inside a fixed overlay, and it is
  // opened WITH a scroll lock — so Lenis would be stopped and would preventDefault its
  // wheel events. Hand them back. (Carousels are covered by allowNestedScroll instead.)
  var NATIVE = '.pswp, .pswp__scroll-wrap';
  function prevent(node) {
    return !!(node && node.matches && node.matches(NATIVE));
  }

  // ---- Sticky header offset for anchor scrolling -------------------------
  // Same source of truth as theme.js: the offset only applies when the header is sticky.
  function headerOffset() {
    if (!document.querySelector('[data-header-sticky="sticky"]')) { return 0; }
    var header = document.querySelector('[data-header-height]');
    return header ? Math.round(header.getBoundingClientRect().height) : 0;
  }

  // ---- Anchor links ------------------------------------------------------
  function anchorTarget(link) {
    var hash = link.hash;
    if (!hash || hash.length < 2) { return null; }
    var id;
    try { id = decodeURIComponent(hash.slice(1)); } catch (e) { id = hash.slice(1); }
    // getElementById, never querySelector: `href="#!"` (sections/header.liquid) is a legal
    // fragment but an invalid CSS selector, and querySelector would throw on it.
    var el = document.getElementById(id);
    // Lenis only resolves HTMLElement targets — an id belonging to an SVG node (the
    // phosphor-icons sprite is full of them) would silently scroll nowhere after we had
    // already cancelled the click. Let the browser keep those.
    return el instanceof HTMLElement ? el : null;
  }

  var FOCUSABLE = 'a[href], button, input, select, textarea, summary, [tabindex], [contenteditable]';

  // preventDefault() drops the browser's sequential-focus starting point, which would
  // silently break the "skip to content" link. Move focus ourselves instead — without
  // scrolling, so Lenis stays the only thing animating. (An unsupported `preventScroll`
  // is ignored rather than thrown, so no guard is needed around it.)
  function focusTarget(el) {
    if (el.matches(FOCUSABLE)) {
      el.focus({ preventScroll: true });
      return;
    }
    // Not focusable on its own: borrow a tabindex for the duration of the visit.
    el.setAttribute('tabindex', '-1');
    el.focus({ preventScroll: true });
    el.addEventListener('blur', function drop() {
      el.removeEventListener('blur', drop);
      el.removeAttribute('tabindex');
    });
  }

  // A plain left-click on a link pointing at a fragment of THIS exact document.
  function isInPageClick(event) {
    if (event.defaultPrevented) { return false; } // a theme handler owns it (`#recover`, tabs…)
    if (event.button !== 0) { return false; }
    return !(event.metaKey || event.ctrlKey || event.shiftKey || event.altKey);
  }

  function inPageLink(event) {
    var link = event.target && event.target.closest ? event.target.closest('a[href]') : null;
    if (!link) { return null; }
    if (link.hasAttribute('download')) { return null; }
    if (link.target && link.target !== '_self') { return null; }
    // Same document only — the same hash on another URL is a real navigation.
    if (link.host !== window.location.host) { return null; }
    if (link.pathname !== window.location.pathname) { return null; }
    if (link.search !== window.location.search) { return null; }
    return link;
  }

  function onAnchorClick(event) {
    if (!lenis || lenis.isStopped) { return; }
    if (!isInPageClick(event)) { return; }

    var link = inPageLink(event);
    if (!link) { return; }

    var target = anchorTarget(link);
    if (!target) { return; }

    event.preventDefault();
    if (window.history && window.history.pushState && link.hash !== window.location.hash) {
      window.history.pushState(null, '', link.hash);
    }
    focusTarget(target);
    lenis.scrollTo(target, { offset: -headerOffset() });
  }

  // ---- Programmatic scrolls ---------------------------------------------
  // theme.js scrolls the window with window.scroll/scrollTo({behavior:'smooth'}) in several
  // places (accordion into view, collection filtering, the hero/video "scroll down" button).
  // Left alone they run the BROWSER's smooth scroll, which is a second animation engine
  // fighting Lenis whenever the visitor touches the wheel mid-flight. Route just that case
  // through Lenis; every other call signature (instant, 2-arg, horizontal) passes straight
  // through, and Lenis re-syncs from the native scroll event.
  function patchProgrammaticScroll() {
    var nativeScrollTo = window.scrollTo;
    var nativeScroll = window.scroll;

    function routable(opts) {
      return !!(lenis && !lenis.isStopped && opts && typeof opts === 'object' &&
        opts.behavior === 'smooth' && typeof opts.top === 'number' && !opts.left);
    }
    // `arguments` (rather than named params or a rest arg) on purpose: window.scrollTo has
    // two overloads, and calling the 2-arg one with explicit `undefined`s would scroll to
    // 0,0 instead of doing nothing. Forwarding `arguments` preserves the original arity.
    function wrap(native) {
      return function () {
        var opts = arguments[0];
        if (routable(opts)) {
          lenis.scrollTo(opts.top);
          return;
        }
        return native.apply(window, arguments);
      };
    }

    window.scrollTo = wrap(nativeScrollTo);
    window.scroll = wrap(nativeScroll);

    return function restore() {
      window.scrollTo = nativeScrollTo;
      window.scroll = nativeScroll;
    };
  }

  // ---- Scroll lock mirroring --------------------------------------------
  function syncLock() {
    if (!lenis) { return; }
    if (html.hasAttribute('data-scroll-locked')) {
      lenis.stop();
    } else {
      lenis.start();
    }
  }

  // ---- Boot / teardown --------------------------------------------------
  function boot() {
    if (!window.Lenis || lenis) { return; }

    lenis = new window.Lenis({
      autoRaf: true,              // Lenis owns its rAF loop — nothing to wire in the theme
      lerp: LERP,                 // framerate-independent smoothing (Lenis default 0.1)
      wheelMultiplier: WHEEL,
      smoothWheel: true,
      syncTouch: false,           // touch stays 100% native — see the header note
      gestureOrientation: 'vertical',
      allowNestedScroll: true,    // the horizontal-scroller guard — see the header note
      overscroll: true,
      autoResize: true,           // ResizeObserver on the content: lazy images, AJAX sections
      anchors: false,             // handled above (sticky-header offset + theme bail-outs)
      stopInertiaOnNavigate: true,// kills leftover inertia across the View Transitions nav
      prevent: prevent
    });

    window.themeLenis = lenis;
    if (window.theme) { window.theme.lenis = lenis; }

    restoreScrollTo = patchProgrammaticScroll();

    lockObserver = new MutationObserver(syncLock);
    lockObserver.observe(html, { attributes: true, attributeFilter: ['data-scroll-locked'] });
    syncLock(); // a drawer may already be open (e.g. ?diag_open_cart=1)

    if (ANCHORS) {
      document.addEventListener('click', onAnchorClick);
    }

    // Sections re-render (theme editor, Section Rendering API) → re-measure immediately
    // instead of waiting out the 250 ms autoResize debounce.
    document.addEventListener('shopify:section:load', resize);
    document.addEventListener('shopify:section:unload', resize);
    document.addEventListener('shopify:section:reorder', resize);
  }

  function resize() {
    if (lenis) { lenis.resize(); }
  }

  function teardown() {
    if (!lenis) { return; }
    if (lockObserver) { lockObserver.disconnect(); lockObserver = null; }
    if (restoreScrollTo) { restoreScrollTo(); restoreScrollTo = null; }
    document.removeEventListener('click', onAnchorClick);
    document.removeEventListener('shopify:section:load', resize);
    document.removeEventListener('shopify:section:unload', resize);
    document.removeEventListener('shopify:section:reorder', resize);
    lenis.destroy(); // also strips the .lenis* classes, so the CSS overrides lift with it
    lenis = null;
    window.themeLenis = null;
    if (window.theme) { window.theme.lenis = null; }
  }

  // Someone turning "reduce motion" on mid-session gets native scrolling back at once.
  function onReduceChange() {
    if (reduceMQ.matches) { teardown(); } else { boot(); }
  }
  if (typeof reduceMQ.addEventListener === 'function') {
    reduceMQ.addEventListener('change', onReduceChange);
  } else if (typeof reduceMQ.addListener === 'function') {
    reduceMQ.addListener(onReduceChange);
  }

  // ---- Load the self-hosted library ------------------------------------
  // A dynamically inserted <script> is async by definition, so there is nothing to defer:
  // this file already runs from a deferred tag, i.e. after theme.js and a parsed DOM.
  var s = document.createElement('script');
  s.src = SRC;
  s.addEventListener('load', boot);
  s.addEventListener('error', function () {
    /* native scrolling stays; the .lenis class is never added, so the CSS overrides
       in snippets/smooth-scroll-style.liquid never apply either. */
  });
  document.head.appendChild(s);
})();
